/**
 * The built things, one per stage.
 *
 * What the timeline's cards became. Each item is a stage split into a media
 * half and a prose half, alternating side down the page, with an optional code
 * listing under the prose and an optional spec band under that.
 *
 * The difference from a timeline is not cosmetic: there is no rail, no step
 * number, and no implied order beyond the order they are written in. A reader
 * who lands on one from the menu gets a complete thing rather than "step 4 of
 * 9" with no idea what four means.
 */

import type { BlockRenderer, MediaItem } from '@remidus/web-core'

import { claimAnchor } from '../anchors'
import { renderCode } from './code'
import { figureFor, paragraphs, resolveAll, specList } from './prose'

interface Feature {
  kicker?: string
  title?: string
  text?: string
  media_ids?: string[] | string
  media_id?: string
  code?: { file?: string; lang?: string; note?: string; code?: string }
  specs?: { label?: string; value?: string }[]
  /** Which signal this belongs to, if any. Tints the kicker's mark only. */
  signal?: 'household' | 'recycling'
}

type Role = 'single' | 'lead' | 'side' | 'row'

function ratio(item: MediaItem): number {
  return item.width && item.height ? item.width / item.height : 4 / 3
}

function isPortrait(item: MediaItem): boolean {
  return ratio(item) < 0.9
}

/**
 * The frame a shot is shown in.
 *
 * The photographs are phone shots: portraits near 1:2, landscapes near 2:1,
 * and a set that mixes them cannot share an edge at their own shapes. So a
 * portrait is cut to 4:5 — the print ratio — and a landscape keeps its own
 * shape in a lead but is held to 3:2 in a row, where a very wide crop would
 * otherwise starve its neighbours of height. See DESIGN.md, The Print Rule.
 */
function frameFor(item: MediaItem, role: Role): number {
  const ar = ratio(item)
  if (isPortrait(item)) return 4 / 5
  if (role === 'row') return Math.min(ar, 3 / 2)
  return Math.min(ar, 2.2)
}

function shot(item: MediaItem, role: Role): HTMLElement {
  const figure = figureFor(item)
  figure.classList.add('skin-shot')
  figure.dataset.orientation = isPortrait(item) ? 'portrait' : 'landscape'
  figure.style.setProperty('--frame', frameFor(item, role).toFixed(4))
  // Core sizes the element by the asset's own ratio so the page does not shift
  // while it loads. The frame does the same job in our shape, so the inline
  // value comes off and the stylesheet decides — including when a side lead
  // has no fixed ratio at all and simply fills its column.
  figure.querySelector<HTMLElement>(':scope > picture, :scope > video')?.style.removeProperty('aspect-ratio')
  return figure
}

/**
 * One shot stands alone. Two share a row at one height. Three or more are a
 * lead and a row: a landscape lead sits above its row, a portrait lead stands
 * beside a column, stretched to its height, so every set fills its column
 * edge to edge and nothing is left hanging under a neighbour.
 */
function mediaFor(items: MediaItem[]): HTMLElement | null {
  if (items.length === 0) return null
  // The holder is the size container the set's narrow layout queries, so the
  // set has to be a child of it rather than the same element.
  const holder = document.createElement('div')
  holder.className = 'skin-feature__media'

  if (items.length === 1) {
    holder.append(shot(items[0], 'single'))
    return holder
  }

  const set = document.createElement('div')
  set.className = 'skin-set'
  holder.append(set)

  if (items.length === 2) {
    set.classList.add('skin-set--row')
    for (const item of items) set.append(shot(item, 'row'))
    return holder
  }

  const [lead, ...rest] = items
  const side = isPortrait(lead)
  set.classList.add(side ? 'skin-set--side' : 'skin-set--stack')
  set.append(shot(lead, side ? 'side' : 'lead'))
  const row = document.createElement('div')
  row.className = 'skin-set__rest'
  for (const item of rest) row.append(shot(item, 'row'))
  set.append(row)
  return holder
}

export const renderFeatures: BlockRenderer = (block, ctx) => {
  const items = (block.data?.items ?? []) as Feature[]
  if (!Array.isArray(items) || items.length === 0) return null

  const run = document.createElement('div')
  run.className = 'skin-features'

  for (const [index, feature] of items.entries()) {
    const stage = document.createElement('section')
    stage.className = 'skin-stage skin-stage--feature'
    // Alternating, so the reader's eye crosses the page rather than running
    // down one edge of it.
    stage.dataset.side = index % 2 === 0 ? 'media-right' : 'media-left'
    if (feature.signal) stage.dataset.signal = feature.signal
    stage.dataset.remItem = String(index)
    claimAnchor(stage, feature.title ?? feature.kicker ?? `Feature ${index + 1}`, feature.kicker)

    const words = document.createElement('div')
    words.className = 'skin-feature__words'

    if (feature.kicker) {
      const kicker = document.createElement('p')
      kicker.className = 'skin-kicker'
      kicker.textContent = feature.kicker
      kicker.dataset.remField = `items.${index}.kicker`
      words.append(kicker)
    }
    if (feature.title) {
      const title = document.createElement('h2')
      title.className = 'skin-stage__title'
      title.textContent = feature.title
      title.dataset.remField = `items.${index}.title`
      words.append(title)
    }
    if (feature.text) {
      const prose = document.createElement('div')
      prose.className = 'skin-feature__prose'
      prose.dataset.remField = `items.${index}.text`
      prose.setAttribute('data-rem-field-multiline', '')
      paragraphs(prose, feature.text)
      words.append(prose)
    }
    if (feature.code?.code) {
      const listing = renderCode({ ...block, data: feature.code }, ctx)
      if (listing) words.append(listing)
    }
    const specs = specList(feature.specs, `items.${index}.specs`)
    if (specs) words.append(specs)

    stage.append(words)
    const media = mediaFor(resolveAll(feature, ctx))
    if (media) stage.append(media)

    run.append(stage)
  }

  return run
}
